import { useCallback, useEffect, useState } from 'react';
import { Copy, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api';

export const copy = async (text, what) => {
  try { await navigator.clipboard.writeText(text); toast.success(`${what} copié`); }
  catch { toast.error('Copie impossible : sélectionne le texte'); }
};

export function EntryForm({ initial, withEn, withTheme, onSave, onCancel }) {
  const [e, setE] = useState({ title: '', text: '', text_en: '', theme: '', ...initial });
  const set = (k) => (ev) => setE(x => ({ ...x, [k]: ev.target.value }));
  const field = 'w-full rounded-xl border border-slate-200 px-3 py-2 text-base bg-white';
  return (
    <div className="bg-slate-50 rounded-2xl border border-slate-200 p-3 space-y-2" data-testid="entry-form">
      {withTheme && <input value={e.theme || ''} onChange={set('theme')} placeholder="Thème (ex. Messages, Prix…)" className={field} />}
      <input value={e.title} onChange={set('title')} placeholder="Titre" className={field} data-testid="entry-title" />
      <textarea value={e.text} onChange={set('text')} rows={6} placeholder={withEn ? 'Texte en français' : 'Texte'} className={field} data-testid="entry-text" />
      {withEn && <textarea value={e.text_en || ''} onChange={set('text_en')} rows={4} placeholder="Texte en anglais (facultatif)" className={field} />}
      <div className="flex gap-2">
        <button type="button" disabled={!e.title.trim()} data-testid="entry-save"
          onClick={() => onSave({ title: e.title, text: e.text, text_en: withEn ? (e.text_en || null) : null, theme: withTheme ? (e.theme || null) : null })}
          className="flex-1 rounded-xl bg-[#0288D1] text-white font-semibold py-2 disabled:opacity-40">Enregistrer</button>
        <button type="button" onClick={onCancel} className="rounded-xl border border-slate-200 px-4 font-semibold text-slate-600">Annuler</button>
      </div>
    </div>
  );
}

/** Liste de textes modifiables (Messages types, Mes règles, Process de Claude). */
export default function EntryList({ space, withEn = false, withTheme = false, copyable = false, intro, addLabel = 'Ajouter', canImport = false }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);  // 'new' | id
  const [confirmDel, setConfirmDel] = useState(null);
  const load = useCallback(() => api.get(`/memo/${space}`).then(setItems).catch(e => toast.error(e.message)), [space]);
  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    try {
      if (editing === 'new') await api.post(`/memo/${space}`, body); else await api.put(`/memo/${space}/${editing}`, body);
      setEditing(null); load();
    } catch (e) { toast.error(e.message); }
  };
  const remove = async (id) => { try { await api.delete(`/memo/${space}/${id}`); setConfirmDel(null); load(); } catch (e) { toast.error(e.message); } };
  const importGoogle = async () => {
    try { const r = await api.post('/memo-import/google'); toast.success(`Repris de Google : ${Object.values(r.ajoutes).reduce((a, b) => a + b, 0)} élément(s)`); load(); }
    catch (e) { toast.error(e.message); }
  };

  const low = q.trim().toLowerCase();
  const shown = (items || []).filter(e => !low || `${e.title} ${e.text} ${e.text_en || ''} ${e.theme || ''}`.toLowerCase().includes(low));
  let lastTheme = null;

  return (
    <div className="space-y-3" data-testid={`memo-${space}`}>
      {intro && <p className="text-sm text-slate-500">{intro}</p>}
      <div className="flex gap-2">
        <div className="flex-1 flex items-center gap-2 bg-white rounded-xl border border-slate-200 px-3">
          <Search size={15} className="text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Chercher…" className="flex-1 py-2 text-base outline-none bg-transparent" />
        </div>
        <button type="button" onClick={() => setEditing('new')} data-testid="entry-add"
          className="rounded-xl bg-[#26A69A] text-white font-semibold px-3 flex items-center gap-1"><Plus size={16} /> {addLabel}</button>
      </div>
      {canImport && items?.length === 0 && (
        <button type="button" onClick={importGoogle} className="w-full rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-[#0288D1]">
          Reprendre mes listes depuis Google Tasks</button>
      )}
      {editing === 'new' && <EntryForm withEn={withEn} withTheme={withTheme} onSave={save} onCancel={() => setEditing(null)} />}
      {items === null ? <p className="text-slate-400 text-sm">…</p> : shown.length === 0 && !editing ? <p className="text-slate-400 text-sm">Rien pour l'instant.</p> : null}
      {shown.map(e => {
        const head = withTheme && e.theme !== lastTheme ? (lastTheme = e.theme) || 'Sans thème' : null;
        return (
          <div key={e.id}>
            {head && <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-4 mb-1">{head}</h3>}
            {editing === e.id ? (
              <EntryForm initial={e} withEn={withEn} withTheme={withTheme} onSave={save} onCancel={() => setEditing(null)} />
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2" data-testid="entry">
                <div className="flex items-start gap-2">
                  <h4 className="font-bold text-slate-900 flex-1">{e.title}</h4>
                  <button type="button" onClick={() => setEditing(e.id)} className="text-slate-400 hover:text-slate-600" aria-label="Modifier"><Pencil size={15} /></button>
                  <button type="button" onClick={() => setConfirmDel(e.id)} className="text-slate-400 hover:text-red-600" aria-label="Supprimer"><Trash2 size={15} /></button>
                </div>
                {e.text && <p className="text-sm text-slate-700 whitespace-pre-line line-clamp-6">{e.text}</p>}
                {e.text_en && <p className="text-sm text-slate-500 whitespace-pre-line line-clamp-3 border-t border-slate-100 pt-2">🇬🇧 {e.text_en}</p>}
                {copyable && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button type="button" onClick={() => copy(e.text, 'Texte')} data-testid="copy-fr"
                      className="rounded-xl bg-[#0288D1] text-white text-sm font-semibold px-3 py-1.5 flex items-center gap-1"><Copy size={14} /> Copier{e.text_en ? ' FR' : ''}</button>
                    {e.text_en && <>
                      <button type="button" onClick={() => copy(e.text_en, 'Texte anglais')} className="rounded-xl border border-[#0288D1] text-[#0288D1] text-sm font-semibold px-3 py-1.5">Copier EN</button>
                      <button type="button" onClick={() => copy(`🇫🇷 ${e.text}\n\n🇬🇧 ${e.text_en}`, 'FR + EN')} className="rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold px-3 py-1.5">Copier les deux</button>
                    </>}
                  </div>
                )}
                {confirmDel === e.id && (
                  <div className="flex items-center gap-2 text-sm bg-red-50 rounded-xl px-3 py-2">
                    Supprimer « {e.title} » ?
                    <button type="button" onClick={() => remove(e.id)} className="ml-auto font-bold text-red-600">Supprimer</button>
                    <button type="button" onClick={() => setConfirmDel(null)} className="font-semibold text-slate-500">Non</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
